

























































import { Component, Prop, Inject } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import PlushieStore from "@/modules/plushie/store";
import dataStore from "@/store";
import IdGenerator from "@/lib/services/id-generator";
import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import { Dictionary } from "@/lib/Dictionary.type";

import ProductionTimeNormEditItem from "./EditItem.vue";

import ProductionTimeNorm from "../../production-time-norm.model";
import FactoryStore from "../../store";

interface EditItem {
  value?: ProductionTimeNorm;
  id: string;
  isNew: boolean;
  isDirty: boolean;
}

@Component({
  components: {
    FormErrors,
    LoadingSpinner,
    ProductionTimeNormEditItem,
    SubmitButton,
  },
})
export default class ProductionTimeNormEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  private fIsLoading = false;

  private fFactoryDataStore: FactoryStore;
  private fPlushieDataStore: PlushieStore;

  private fEditItems: EditItem[] = [];

  get existingProducts(): string[] {
    return this.editItems
      .map((item) => item.value)
      .filter((item): item is ProductionTimeNorm => item !== undefined)
      .map((item) => item.product);
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get editItems(): EditItem[] {
    return this.fEditItems;
  }

  constructor() {
    super();
    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
  }

  public addItem(): void {
    const id = this.fIdGenerator.getId();

    this.fEditItems.push({
      value: undefined,
      id: id,
      isDirty: false,
      isNew: true,
    });
  }

  public onItemChange(
    value: ProductionTimeNorm | undefined,
    index: number
  ): void {
    const item = this.fEditItems[index];
    item.value = value;
    item.isDirty = true;
  }

  public onCancelClick(): void {
    this.$emit("cancel");
  }

  public onItemDelete(item: EditItem): void {
    const index = this.fEditItems.indexOf(item);
    this.fEditItems.splice(index, 1);
  }

  public replaceProductValues(productDaysDictionary: Dictionary<number>): void {
    this.fEditItems.forEach((item, index) => {
      const normItem = item.value;
      if (!normItem) {
        return;
      }

      const newDaysValue = productDaysDictionary[normItem.product];

      if (newDaysValue === undefined || newDaysValue === normItem.days) {
        return;
      }

      const updatedTimeNorm = new ProductionTimeNorm(
        normItem.id,
        normItem.factory,
        normItem.product,
        newDaysValue
      );

      this.onItemChange(updatedTimeNorm, index);
    });
  }

  protected async performSubmit(): Promise<void> {
    const values = this.fEditItems
      .map((item) => item.value)
      .filter((item): item is ProductionTimeNorm => item !== undefined);

    const items = await this.fFactoryDataStore.saveProductionTimeNormsForFactory(
      {
        factoryId: this.factoryId,
        values,
      }
    );

    this.fEditItems = this.createEditItems(items);

    this.$emit("updated");
  }

  protected async created(): Promise<void> {
    this.fIsLoading = true;

    try {
      const [norms] = await Promise.all([
        this.fFactoryDataStore.loadProductionTimeNormsByFactoryId({
          factoryId: this.factoryId,
          useCache: false,
          isDisablePagination: true,
        }),
        this.fPlushieDataStore.loadProducts(),
      ]);

      this.fEditItems = this.createEditItems(norms);
    } finally {
      this.fIsLoading = false;
    }
  }

  private createEditItems(values: ProductionTimeNorm[]): EditItem[] {
    const editItems: EditItem[] = [];

    const products = this.fPlushieDataStore.activeProductsList;

    products.forEach((product) => {
      const item = values.find((value) => value.product === product.id);

      if (!item) {
        return;
      }

      editItems.push({
        value: item,
        id: item.id,
        isNew: false,
        isDirty: false,
      });
    });

    return editItems;
  }
}
